import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Twitter } from 'lucide-react';

const PrivacyPolicy = () => (
  <div className="min-h-screen bg-[#020817] text-white antialiased">
    {/* Nav */}
    <header className="sticky top-0 z-40 border-b border-white/[0.05] bg-[#020817]/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 sm:px-6">
        <Link to="/" className="flex items-center gap-2.5" aria-label="Brownlow Genius home">
          <img src="/favicon.svg" alt="" className="h-7 w-7 object-contain" />
          <span className="text-[15px] font-semibold tracking-tight text-white">Brownlow Genius</span>
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[13px] font-medium text-slate-300 transition-colors hover:bg-white/[0.04] hover:text-white"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.5} />
          Back to home
        </Link>
      </div>
    </header>

    {/* Header */}
    <div className="relative overflow-hidden">
      <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-0 h-[400px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-500/[0.08] blur-[160px]" />
      <div className="relative mx-auto max-w-6xl px-5 pb-6 pt-16 text-center sm:px-6 md:pt-24">
        <h1 className="text-4xl font-bold tracking-[-0.02em] sm:text-5xl">
          <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
            Privacy Policy
          </span>
        </h1>
        <p className="mt-3 text-sm text-slate-500">Last updated: 13 September 2026</p>
      </div>
    </div>

    {/* Content */}
    <div className="mx-auto max-w-3xl px-5 pb-20 pt-8 sm:px-6 md:pb-28">
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7 md:p-10">
        <div className="space-y-10 text-[15px] leading-relaxed text-slate-400">
          <section>
            <div className="space-y-4">
              <p>
                This Privacy Policy explains how SnipeAIO Pty Ltd ("SnipeAIO", "we", "us", or "our") collects, uses, discloses, stores, and protects personal information in connection with the Brownlow Genius platform (the "Service").
              </p>
              <p>
                We handle personal information in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles. This Privacy Policy should be read together with our{' '}
                <Link to="/terms" className="font-medium text-emerald-400 transition-colors hover:text-emerald-300">Terms of Service</Link>.
              </p>
              <p className="font-medium text-slate-200">
                By creating an account and using the Service, you acknowledge that you have read and understood this Privacy Policy.
              </p>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">1. Information We Collect</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-slate-200">1.1 Account Information</h3>
                <div className="mt-1 space-y-3">
                  <p>When you create an account, we collect:</p>
                  <ul className="list-disc space-y-2 pl-6">
                    <li><strong className="text-slate-200">Email address</strong> — used to identify your account, authenticate you, and send you account and Service communications.</li>
                    <li><strong className="text-slate-200">Password</strong> — stored only as a salted cryptographic hash. We cannot view, retrieve, or recover your actual password.</li>
                    <li><strong className="text-slate-200">Account metadata</strong> — such as the date your account was created, your access tier (currently free), and your account status.</li>
                  </ul>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-slate-200">1.2 Communication Preferences</h3>
                <div className="mt-1 space-y-3">
                  <p>We record whether you have consented to receive marketing communications from us, and when that consent was given or withdrawn.</p>
                  <p>We retain these records so that we can demonstrate compliance with our obligations under the Spam Act 2003 (Cth) and applicable privacy law.</p>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-slate-200">1.3 Usage and Analytics Information</h3>
                <div className="mt-1 space-y-3">
                  <p>We use Google Analytics to understand how the Service is used. This may include pages viewed, time spent on the Service, referring pages, browser type, device type, and approximate geographic location at a country or region level.</p>
                  <p>This information is generally reported to us in aggregated form and is not used by us to identify individual users.</p>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-slate-200">1.4 Technical Information</h3>
                <p className="mt-1">Our servers automatically record technical information such as IP addresses, browser type and version, operating system, device information, and request logs. We use this information for security, abuse prevention, diagnostics, and to maintain the reliability of the Service.</p>
              </div>
              <div>
                <h3 className="font-semibold text-slate-200">1.5 Information We Do Not Collect</h3>
                <div className="mt-1 space-y-3">
                  <ul className="list-disc space-y-2 pl-6">
                    <li>We do not collect payment card or banking details. The Service is currently free and we do not process payments.</li>
                    <li>We do not require your real name, phone number, or postal address in order to create an account or use the Service.</li>
                    <li>We do not collect sensitive information as defined in the Privacy Act 1988 (Cth), including information about your health, racial or ethnic origin, political opinions, religious beliefs, sexual orientation, criminal record, or biometric data.</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">2. How We Collect Information</h2>
            <div className="space-y-3">
              <p>We collect personal information directly from you when you register for an account, sign in, update your account details, adjust your communication preferences, or contact us.</p>
              <p>We also collect information automatically as you use the Service, through server logs, cookies, browser storage, and analytics tools.</p>
              <p>We do not purchase or acquire personal information about you from third-party data brokers or list providers.</p>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">3. Why We Collect and How We Use Information</h2>
            <div className="space-y-3">
              <p>We collect, hold, and use personal information for the following purposes:</p>
              <ul className="list-disc space-y-2 pl-6">
                <li>to create, authenticate, and maintain your account;</li>
                <li>to provide, operate, and support the Service and its features;</li>
                <li>to send you communications reasonably necessary to administer your account and the Service, including account verification, password resets, security notifications, and changes to our Terms of Service or this Privacy Policy;</li>
                <li>to send you marketing communications, but only where you have given us your consent to do so;</li>
                <li>to secure the Service and to detect, investigate, and prevent fraud, abuse, unauthorised access, and breaches of our Terms of Service;</li>
                <li>to analyse usage patterns so that we can improve and develop the Service;</li>
                <li>to respond to your enquiries, requests, and complaints; and</li>
                <li>to comply with our legal obligations and to establish, exercise, or defend legal claims.</li>
              </ul>
              <p>We do not sell, rent, or trade your personal information.</p>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">4. Marketing Communications</h2>
            <div className="space-y-3">
              <p>We will only send you marketing communications where you have given us your express consent. At registration this is an optional checkbox which is not selected by default, and leaving it unselected does not affect your ability to create an account or use the Service.</p>
              <p>Where you have consented, we may use your email address to send you information about new Brownlow Genius products, features, updates, promotions, or offers.</p>
              <p>
                You may withdraw your consent at any time by using the unsubscribe link in any marketing email we send you, or by{' '}
                <a href="mailto:info@snipeaio.com" className="font-medium text-emerald-400 transition-colors hover:text-emerald-300">contacting us here</a>.
                {' '}We will action your request promptly.
              </p>
              <p>Withdrawing your consent to marketing communications will not stop communications that are reasonably necessary to operate, administer, or secure your account or the Service.</p>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">5. Cookies, Analytics, and Browser Storage</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-slate-200">5.1 Essential Browser Storage</h3>
                <div className="mt-1 space-y-3">
                  <p>When you sign in, we store an authentication token in your browser&apos;s local storage so that you remain signed in between pages and visits. We may also store short-lived session values, such as a flag indicating that your session has expired.</p>
                  <p>This storage is necessary for the Service to function and is not used for advertising or cross-site tracking. Clearing your browser storage will sign you out.</p>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-slate-200">5.2 Google Analytics</h3>
                <div className="mt-1 space-y-3">
                  <p>Google Analytics may set cookies to measure how the Service is used.</p>
                  <ul className="list-disc space-y-2 pl-6">
                    <li>You can opt out of Google Analytics across all websites by installing the{' '}
                      <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="font-medium text-emerald-400 transition-colors hover:text-emerald-300">Google Analytics opt-out browser add-on</a>.
                    </li>
                    <li>You can read more about how Google handles data at{' '}
                      <a href="https://policies.google.com/privacy/partners" target="_blank" rel="noopener noreferrer" className="font-medium text-emerald-400 transition-colors hover:text-emerald-300">Google&apos;s privacy and terms for partners</a>.
                    </li>
                  </ul>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-slate-200">5.3 Managing Cookies</h3>
                <p className="mt-1">You can control or block cookies through your browser settings. Please note that blocking essential cookies or browser storage may prevent you from signing in to or using parts of the Service.</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">6. Disclosure of Personal Information</h2>
            <div className="space-y-3">
              <p>We do not sell your personal information. We may disclose personal information in the following circumstances:</p>
              <ul className="list-disc space-y-2 pl-6">
                <li><strong className="text-slate-200">Service providers:</strong> to providers who host our infrastructure, store our data, deliver our emails, or provide analytics and security services, so that they can perform those services for us.</li>
                <li><strong className="text-slate-200">Legal and safety reasons:</strong> where required or authorised by law, a court order, or a regulatory request, or where reasonably necessary to protect the rights, property, or safety of SnipeAIO, our users, or others.</li>
                <li><strong className="text-slate-200">Business transfers:</strong> if SnipeAIO is involved in a merger, acquisition, restructure, or sale of assets, personal information may be transferred as part of that transaction, subject to the acquirer handling it consistently with this Privacy Policy.</li>
                <li><strong className="text-slate-200">With your consent:</strong> where you have otherwise agreed to the disclosure.</li>
              </ul>
              <p>We require our service providers to use personal information only for the purpose of providing their services to us, and to protect it appropriately.</p>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">7. Overseas Disclosure</h2>
            <div className="space-y-3">
              <p>Some of our service providers store or process information outside Australia. This includes providers located in the United States, such as Google Analytics, and may include cloud hosting and email delivery providers located in the United States or other jurisdictions.</p>
              <p>By using the Service, you acknowledge that your personal information may be transferred to, stored in, or accessed from countries other than Australia.</p>
              <p>We take reasonable steps to ensure that overseas recipients handle your personal information in a manner consistent with the Australian Privacy Principles. However, overseas recipients may be subject to laws that differ from Australian privacy law.</p>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">8. Data Security</h2>
            <div className="space-y-3">
              <p>We implement reasonable technical and organisational measures to protect personal information from misuse, interference, loss, and unauthorised access, modification, or disclosure. These measures include:</p>
              <ul className="list-disc space-y-2 pl-6">
                <li>storing passwords only as salted cryptographic hashes rather than in plain text;</li>
                <li>encrypting data in transit between your browser and our servers;</li>
                <li>restricting access to account data to those who reasonably require it; and</li>
                <li>monitoring for unauthorised access and misuse of the Service.</li>
              </ul>
              <p>No method of transmission over the internet or method of electronic storage is completely secure, and we cannot guarantee absolute security.</p>
              <p>You are responsible for keeping your account credentials confidential. Please notify us if you become aware of any unauthorised access to or use of your account.</p>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">9. Data Retention</h2>
            <div className="space-y-3">
              <p>We retain your account information for as long as your account remains active.</p>
              <p>If you delete your account, or ask us to delete it, we will delete or de-identify your account information within a reasonable period, except where we are required or permitted to retain it — for example to comply with a legal obligation, to resolve a dispute, to prevent fraud or abuse, or to maintain records demonstrating marketing consent and unsubscribe requests.</p>
              <p>Analytics information is retained in accordance with the retention settings of our analytics provider and is generally held in aggregated or de-identified form.</p>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">10. Accessing, Correcting, and Deleting Your Information</h2>
            <div className="space-y-3">
              <p>You may ask us to:</p>
              <ul className="list-disc space-y-2 pl-6">
                <li><strong className="text-slate-200">Access</strong> the personal information we hold about you;</li>
                <li><strong className="text-slate-200">Correct</strong> personal information that is inaccurate, incomplete, or out of date;</li>
                <li><strong className="text-slate-200">Delete</strong> your account and the personal information associated with it; or</li>
                <li><strong className="text-slate-200">Withdraw</strong> your consent to receive marketing communications.</li>
              </ul>
              <p>
                To make a request, please{' '}
                <a href="mailto:info@snipeaio.com" className="font-medium text-emerald-400 transition-colors hover:text-emerald-300">contact us here</a>.
                {' '}We may need to verify your identity before actioning your request, and we will respond within a reasonable period, usually within 30 days.
              </p>
              <p>If we are unable to give you access to, correct, or delete your information, we will tell you why in writing, except where it would be unreasonable to do so.</p>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">11. Complaints</h2>
            <div className="space-y-3">
              <p>
                If you believe we have breached the Australian Privacy Principles or mishandled your personal information, please{' '}
                <a href="mailto:info@snipeaio.com" className="font-medium text-emerald-400 transition-colors hover:text-emerald-300">contact us here</a>
                {' '}in the first instance. We will investigate your complaint and respond within a reasonable period, usually within 30 days.
              </p>
              <p>
                If you are not satisfied with our response, you may lodge a complaint with the Office of the Australian Information Commissioner at{' '}
                <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer" className="font-medium text-emerald-400 transition-colors hover:text-emerald-300">oaic.gov.au</a>
                {' '}or by calling 1300 363 992.
              </p>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">12. Age Restriction</h2>
            <div className="space-y-3">
              <p>The Service is intended only for individuals aged 18 years or older, and we do not knowingly collect personal information from anyone under 18.</p>
              <p>If we reasonably believe that we have collected personal information from a person under 18, we may suspend or terminate the account and delete the information.</p>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">13. Changes to This Policy</h2>
            <div className="space-y-3">
              <p>We may update this Privacy Policy from time to time. Where changes are material, we may provide reasonable notice through the Service, by email, or by other appropriate means.</p>
              <p>The current version will always be available on this page, and the "Last updated" date above will reflect when it was last revised.</p>
              <p>Your continued use of the Service after an updated Privacy Policy takes effect indicates that you acknowledge the updated policy.</p>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">14. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy, the personal information we hold about you, or how we handle it, you can{' '}
              <a href="mailto:info@snipeaio.com" className="font-medium text-emerald-400 transition-colors hover:text-emerald-300">contact us here</a>
              {' '}or email SnipeAIO Pty Ltd directly at{' '}
              <a href="mailto:info@snipeaio.com" className="font-medium text-emerald-400 transition-colors hover:text-emerald-300">info@snipeaio.com</a>.
            </p>
          </section>
        </div>
      </div>
    </div>

    {/* Footer */}
    <footer className="border-t border-white/[0.05]">
      <div className="mx-auto max-w-6xl px-5 sm:px-6">
        <div className="flex flex-col items-center gap-6 py-10 text-center md:flex-row md:items-center md:justify-between md:text-left">
          <div className="flex items-center gap-3">
            <img src="/favicon.svg" alt="" className="h-6 w-6 object-contain" />
            <span className="text-sm font-semibold tracking-tight text-white">Brownlow Genius</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[13px] md:justify-end">
            <Link to="/terms" className="text-slate-400 transition-colors hover:text-white">Terms of Service</Link>
            <Link to="/privacy" className="text-slate-400 transition-colors hover:text-white">Privacy Policy</Link>
            <a href="https://x.com/brownlow_genius" target="_blank" rel="noopener noreferrer" aria-label="Follow Brownlow Genius on X" className="text-slate-400 transition-colors hover:text-white">
              <Twitter className="h-4 w-4" aria-hidden="true" />
            </a>
            <span className="text-slate-600">© Brownlow Genius</span>
          </div>
        </div>
        <div className="border-t border-white/[0.05] py-5">
          <p className="text-center text-[13px] leading-relaxed text-slate-500">
            Always remember to gamble responsibly. For free and confidential support call{' '}
            <a href="tel:1800858858" className="font-medium text-slate-400 transition-colors hover:text-white">1800 858 858</a>
            {' '}or visit{' '}
            <a href="https://www.gamblinghelponline.org.au" target="_blank" rel="noopener noreferrer" className="font-medium text-slate-400 transition-colors hover:text-white">gamblinghelponline.org.au</a>.
          </p>
        </div>
      </div>
    </footer>
  </div>
);

export default PrivacyPolicy;
